import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";

// Suppress ResizeObserver loop errors - this is a known browser quirk
// that occurs when ResizeObserver callbacks trigger layout changes
const resizeObserverErrorPattern = /ResizeObserver loop completed with undelivered notifications/i;

// Suppress in console.error
const originalError = console.error;
console.error = (...args) => {
  const errorMessage = args[0];
  if (
    (typeof errorMessage === 'string' && resizeObserverErrorPattern.test(errorMessage)) ||
    (errorMessage?.message && typeof errorMessage.message === 'string' && resizeObserverErrorPattern.test(errorMessage.message)) ||
    (args.some(arg => typeof arg === 'string' && resizeObserverErrorPattern.test(arg)))
  ) {
    return;
  }
  originalError.call(console, ...args);
};

// Suppress in window error handler
if (typeof window !== 'undefined') {
  const originalErrorHandler = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (
      (typeof message === 'string' && resizeObserverErrorPattern.test(message)) ||
      (error?.message && typeof error.message === 'string' && resizeObserverErrorPattern.test(error.message))
    ) {
      return true; // Suppress the error
    }
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    return false;
  };

  // Suppress in unhandledrejection handler (for promise rejections)
  const originalUnhandledRejection = window.onunhandledrejection;
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (
      (typeof reason === 'string' && resizeObserverErrorPattern.test(reason)) ||
      (reason?.message && typeof reason.message === 'string' && resizeObserverErrorPattern.test(reason.message)) ||
      (reason?.stack && typeof reason.stack === 'string' && resizeObserverErrorPattern.test(reason.stack))
    ) {
      event.preventDefault();
      return;
    }
    if (originalUnhandledRejection) {
      originalUnhandledRejection(event);
    }
  });
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error('Failed to find the root element');
}
const root = createRoot(rootElement);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
